import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Grid,
  Chip,
  Divider,
  Paper,
  Avatar,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Link,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Close as CloseIcon,
  Support as SupportIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Schedule as ScheduleIcon,
  Assignment as AssignmentIcon,
  Description as DescriptionIcon,
  AccessTime as AccessTimeIcon,
  CalendarToday as CalendarIcon,
  Add as AddIcon,
  History as HistoryIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { Ticket, IFuncionario } from '../../types';
import { ticketService } from '../../services/ticketService';
import { chamadoMovimentacaoService, ChamadoMovimentacao } from '../../services/chamadoMovimentacaoService';
import funcionarioService from '../../services/funcionarioService';
import NovaMovimentacaoModal from './NovaMovimentacaoModal';
import avatarService from '../../services/avatarService';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import '../../styles/swal-overrides.css';
import { formatarPhone } from '../../utils/formatToView';
import { useNavigate } from 'react-router-dom';

interface ChamadosInfoViewProps {
  open: boolean;
  onClose: () => void;
  ticketId: number | null;
  onSuccess?: () => void;
}

const ChamadosInfoView: React.FC<ChamadosInfoViewProps> = ({
  open,
  onClose,
  ticketId,
  onSuccess,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [movimentacoes, setMovimentacoes] = useState<ChamadoMovimentacao[]>([]);
  const [loadingMovimentacoes, setLoadingMovimentacoes] = useState(false);
  const [movimentacaoModalOpen, setMovimentacaoModalOpen] = useState(false);
  const [atribuidoParaModalOpen, setAtribuidoParaModalOpen] = useState(false);
  const [funcionarios, setFuncionarios] = useState<IFuncionario[]>([]);
  const [loadingFuncionarios, setLoadingFuncionarios] = useState(false);
  const [selectedFuncionarioId, setSelectedFuncionarioId] = useState<number | ''>('');
  const [assigningTicket, setAssigningTicket] = useState(false);
  const [removingAssignment, setRemovingAssignment] = useState(false);

  useEffect(() => {
    if (open && ticketId) {
      loadTicket();
      loadMovimentacoes();
    } else {
      setTicket(null);
      setError(null);
      setMovimentacoes([]);
    }
  }, [open, ticketId]);

  // Carregar funcionários quando o modal de atribuição abrir
  useEffect(() => {
    if (atribuidoParaModalOpen) {
      loadFuncionarios();
    }
  }, [atribuidoParaModalOpen]);

  const loadMovimentacoes = async () => {
    if (!ticketId) return;

    try {
      setLoadingMovimentacoes(true);
      const response = await chamadoMovimentacaoService.getByChamadoId(ticketId);

      if (response.success && response.data) {
        setMovimentacoes(response.data);
      }
    } catch (error: any) {
      console.error('Erro ao carregar movimentações:', error);
    } finally {
      setLoadingMovimentacoes(false);
    }
  };

  const loadFuncionarios = async () => {
    try {
      setLoadingFuncionarios(true);
      const response = await funcionarioService.getFuncionariosAtivos();

      if (response.success && response.data) {
        setFuncionarios(response.data || []);
      }
    } catch (error: any) {
      console.error('Erro ao carregar funcionários:', error);
      toast.error('Erro ao carregar funcionários');
    } finally {
      setLoadingFuncionarios(false);
    }
  };

  const handleAssignTicket = async () => {
    if (!ticket || !selectedFuncionarioId) {
      toast.error('Selecione um funcionário para atribuir');
      return;
    }

    try {
      setAssigningTicket(true);
      const response = await ticketService.assign(ticket.id, Number(selectedFuncionarioId));

      if (response.success && response.data) {
        setTicket(response.data);
        toast.success('Chamado atribuído com sucesso!');
        setAtribuidoParaModalOpen(false);
        setSelectedFuncionarioId('');
        // Recarregar movimentações para verificar se foi criada uma movimentação automática
        loadMovimentacoes();
      } else {
        const errorMessage = response.message || 'Erro ao atribuir chamado';
        toast.error(errorMessage);
      }
    } catch (error: any) {
      console.error('Erro ao atribuir chamado:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao atribuir chamado';
      toast.error(errorMessage);
    } finally {
      setAssigningTicket(false);
    }
  };

  const handleCloseAssignModal = () => {
    setAtribuidoParaModalOpen(false);
    setSelectedFuncionarioId('');
  };

  const handleRemoveAssignment = async () => {
    if (!ticket) return;

    try {
      const result = await Swal.fire({
        title: 'Remover Atribuição',
        text: 'Deseja realmente remover a atribuição deste chamado?',
        icon: 'question',
        customClass: {
          container: 'swal-zindex',
          popup: 'swal-zindex'
        },
        showCancelButton: true,
        confirmButtonText: 'Sim, Remover',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#d32f2f',
        cancelButtonColor: '#6c757d',
      });

      if (result.isConfirmed) {
        setRemovingAssignment(true);
        const response = await ticketService.assign(ticket.id, null);

        if (response.success) {
          toast.success('Atribuição removida com sucesso!');
          loadTicket();
          loadMovimentacoes();
          if (onSuccess) {
            onSuccess();
          }
        } else {
          toast.error(response.message || 'Erro ao remover atribuição');
        }
      }
    } catch (error: any) {
      console.error('Erro ao remover atribuição:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao remover atribuição';
      toast.error(errorMessage);
    } finally {
      setRemovingAssignment(false);
    }
  };

  // Normalizar anexos para sempre ser um array
  const normalizeAnexos = (anexos: any): string[] => {
    if (!anexos) return [];
    if (Array.isArray(anexos)) return anexos;
    if (typeof anexos === 'string') {
      try {
        const parsed = JSON.parse(anexos);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  const loadTicket = async () => {
    if (!ticketId) return;

    try {
      setLoading(true);
      setError(null);
      const response = await ticketService.getById(ticketId);

      if (response.success && response.data) {
        setTicket(response.data);
      } else {
        setError(response.message || 'Erro ao carregar chamado');
      }
    } catch (error: any) {
      console.error('Erro ao carregar chamado:', error);
      setError('Erro ao carregar chamado');
    } finally {
      setLoading(false);
    }
  };

  const handleReturnTicket = async () => {
    if (!ticket) return;

    // Fechar o modal principal antes de abrir o SweetAlert
    onClose();

    try {
      const { value: motivo } = await Swal.fire({
        title: 'Retornar Chamado',
        text: 'Deseja retornar este chamado para o status Aberto?',
        icon: 'question',
        input: 'textarea',
        inputLabel: 'Motivo do retorno',
        inputPlaceholder: 'Descreva o motivo pelo qual este chamado está sendo retornado...',
        inputAttributes: {
          'aria-label': 'Motivo do retorno'
        },
        customClass: {
          container: 'swal-zindex',
          popup: 'swal-zindex'
        },
        showCancelButton: true,
        confirmButtonText: 'Retornar Chamado',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#2e7d32',
        cancelButtonColor: '#6c757d',
        showLoaderOnConfirm: true,
        allowOutsideClick: () => !Swal.isLoading(),
        preConfirm: async (motivo) => {
          if (!motivo || !motivo.trim()) {
            Swal.showValidationMessage('O motivo é obrigatório para retornar o chamado');
            return false;
          }
          try {
            const informacoes = `Chamado retornado: ${motivo.trim()}`;
            const response = await ticketService.retornarTicket(ticket.id, 'aberto', informacoes);

            if (!response.success) {
              throw new Error(response.message || 'Erro ao retornar chamado');
            }

            return response.data;
          } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || 'Erro ao retornar chamado';
            Swal.showValidationMessage(errorMessage);
            throw error;
          }
        }
      });

      if (motivo) {
        setTicket(motivo);
        toast.success('Chamado retornado com sucesso!');
        loadMovimentacoes();

        // Notificar componente pai para atualizar a lista
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error: any) {
      if (error && error.name !== 'AbortError') {
        console.error('Erro ao retornar chamado:', error);
      }
    }
  };

  const handleOpenCloseTicketModal = async () => {
    if (!ticket) return;

    // Fechar o modal principal antes de abrir o SweetAlert
    onClose();

    const showWarning = ticket.status !== 'resolvido';

    try {
      const { value: informacoes } = await Swal.fire({
        title: 'Fechar Chamado',
        text: showWarning
          ? 'Este chamado não está resolvido, deseja fechar assim mesmo?'
          : 'Tem certeza que deseja fechar este chamado?',
        icon: showWarning ? 'warning' : 'question',
        input: 'textarea',
        inputLabel: 'Informações sobre o fechamento',
        inputPlaceholder: 'Descreva as informações sobre o fechamento deste chamado (opcional)',
        inputAttributes: {
          'aria-label': 'Informações sobre o fechamento'
        },
        customClass: {
          container: 'swal-zindex'
        },
        showCancelButton: true,
        confirmButtonText: 'Fechar Chamado',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: '#d32f2f',
        cancelButtonColor: '#6c757d',
        showLoaderOnConfirm: true,
        allowOutsideClick: () => !Swal.isLoading(),
        preConfirm: async (informacoes) => {
          try {
            const response = await ticketService.close(
              ticket.id,
              informacoes && informacoes.trim() ? informacoes.trim() : undefined
            );

            if (!response.success) {
              throw new Error(response.message || 'Erro ao fechar chamado');
            }

            return informacoes;
          } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || 'Erro ao fechar chamado';
            Swal.showValidationMessage(errorMessage);
            throw error;
          }
        }
      });

      if (informacoes !== undefined) {
        // Atualizar o ticket e recarregar movimentações
        const response = await ticketService.getById(ticket.id);
        if (response.success && response.data) {
          setTicket(response.data);
          loadMovimentacoes();
        }
        toast.success('Chamado fechado com sucesso!');

        // Notificar componente pai para atualizar a lista
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error: any) {
      // Swal foi cancelado ou houve erro no preConfirm
      if (error && error.name !== 'AbortError') {
        console.error('Erro ao fechar chamado:', error);
      }
    }
  };

  const handleOpenCancelTicketModal = async () => {
    if (!ticket) return;

    // Fechar o modal principal antes de abrir o SweetAlert
    onClose();

    try {
      const { value: informacoes } = await Swal.fire({
        title: 'Cancelar Chamado',
        text: 'Tem certeza que deseja cancelar este chamado?',
        icon: 'warning',
        input: 'textarea',
        inputLabel: 'Informações sobre o cancelamento',
        inputPlaceholder: 'Descreva o motivo do cancelamento deste chamado (opcional)',
        inputAttributes: {
          'aria-label': 'Informações sobre o cancelamento'
        },
        customClass: {
          container: 'swal-zindex'
        },
        showCancelButton: true,
        confirmButtonText: 'Cancelar Chamado',
        cancelButtonText: 'Voltar',
        confirmButtonColor: '#d32f2f',
        cancelButtonColor: '#6c757d',
        showLoaderOnConfirm: true,
        allowOutsideClick: () => !Swal.isLoading(),
        preConfirm: async (informacoes) => {
          try {
            const response = await ticketService.cancel(
              ticket.id,
              informacoes && informacoes.trim() ? informacoes.trim() : undefined
            );

            if (!response.success) {
              throw new Error(response.message || 'Erro ao cancelar chamado');
            }

            return informacoes;
          } catch (error: any) {
            const errorMessage = error.response?.data?.message || error.message || 'Erro ao cancelar chamado';
            Swal.showValidationMessage(errorMessage);
            throw error;
          }
        }
      });

      if (informacoes !== undefined) {
        // Atualizar o ticket e recarregar movimentações
        const response = await ticketService.getById(ticket.id);
        if (response.success && response.data) {
          setTicket(response.data);
          loadMovimentacoes();
        }
        toast.success('Chamado cancelado com sucesso!');

        // Notificar componente pai para atualizar a lista
        if (onSuccess) {
          onSuccess();
        }
      }
    } catch (error: any) {
      // Swal foi cancelado ou houve erro no preConfirm
      if (error && error.name !== 'AbortError') {
        console.error('Erro ao cancelar chamado:', error);
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'aberto':
        return 'error';
      case 'em_andamento':
        return 'warning';
      case 'resolvido':
        return 'success';
      case 'fechado':
        return 'default';
      case 'cancelado':
        return 'default';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      aberto: 'Aberto',
      em_andamento: 'Em Andamento',
      resolvido: 'Resolvido',
      fechado: 'Fechado',
      cancelado: 'Cancelado',
    };
    return labels[status] || status;
  };

  const getPrioridadeColor = (prioridade: string) => {
    switch (prioridade) {
      case 'baixa':
        return 'default';
      case 'normal':
        return 'info';
      case 'alta':
        return 'warning';
      case 'urgente':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPrioridadeLabel = (prioridade: string) => {
    const labels: Record<string, string> = {
      baixa: 'Baixa',
      normal: 'Normal',
      alta: 'Alta',
      urgente: 'Urgente',
    };
    return labels[prioridade] || prioridade;
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const formatDateOnly = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    } catch {
      return dateString;
    }
  };

  const formatDateTime = (dateString: string | null | undefined): string => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <SupportIcon color="primary" />
            <Typography variant="h6" component="span">
              Detalhes do Chamado
            </Typography>
            {ticket && (
              <Chip
                label={`#${ticket.id.toString().padStart(6, '0')}`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ ml: 2 }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={4}>
            <CircularProgress />
            <Typography variant="body2" sx={{ ml: 2 }}>
              Carregando informações do chamado...
            </Typography>
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : !ticket ? (
          <Alert severity="info">
            Nenhum chamado selecionado
          </Alert>
        ) : (
          <Box>
            {/* Status e Prioridade */}
            <Box display="flex" gap={1.5} mb={2} flexWrap="wrap">
              <Chip
                label={getStatusLabel(ticket.status)}
                color={getStatusColor(ticket.status) as any}
                size="small"
                icon={<AssignmentIcon />}
              />
              <Chip
                label={getPrioridadeLabel(ticket.prioridade)}
                color={getPrioridadeColor(ticket.prioridade) as any}
                size="small"
                icon={<AccessTimeIcon />}
              />
              {ticket.assunto && (
                <Chip
                  label={ticket.assunto.nome}
                  color="primary"
                  size="small"
                  icon={<AssignmentIcon />}
                />
              )}
            </Box>

            <Grid container spacing={2}>
              {/* Informações Principais */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? '#070b14' : 'grey.50',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom fontWeight="bold" color="primary" sx={{ mb: 0.5 }}>
                    <SupportIcon sx={{ mr: 0.5, verticalAlign: 'middle', fontSize: '1rem' }} />
                    Informações Principais
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Grid container spacing={1.5}>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                        Descrição
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {ticket.descricao}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Divider sx={{ my: 1 }} />
                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        <ScheduleIcon sx={{ mr: 0.5, fontSize: '0.9rem', verticalAlign: 'middle' }} />
                        Criado em
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(ticket.createdAt)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        <AccessTimeIcon sx={{ mr: 0.5, fontSize: '0.9rem', verticalAlign: 'middle' }} />
                        Atualizado em
                      </Typography>
                      <Typography variant="body2">
                        {formatDate(ticket.updatedAt)}
                      </Typography>
                    </Grid>
                    {ticket.data_agendamento && (
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          <CalendarIcon sx={{ mr: 0.5, fontSize: '0.9rem', verticalAlign: 'middle' }} />
                          Agendamento
                        </Typography>
                        <Typography variant="body2" fontWeight="medium" color="primary">
                          {formatDateTime(ticket.data_agendamento)}
                        </Typography>
                      </Grid>
                    )}
                    {ticket.data_fechamento && (
                      <Grid item xs={12} sm={4}>
                        <Typography variant="caption" color="text.secondary" display="block">
                          <CloseIcon sx={{ mr: 0.5, fontSize: '0.9rem', verticalAlign: 'middle' }} />
                          Fechado em
                        </Typography>
                        <Typography variant="body2" fontWeight="medium" color="error">
                          {formatDateTime(ticket.data_fechamento)}
                        </Typography>
                      </Grid>
                    )}
                  </Grid>
                </Paper>
              </Grid>

              {/* Informações do Cliente */}
              <Grid item xs={12} sm={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? '#070b14' : 'grey.50',
                    border: '1px solid',
                    borderColor: 'divider',
                    height: '100%',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" display="block">
                    <BusinessIcon sx={{ mr: 0.5, verticalAlign: 'middle', fontSize: '1rem' }} />
                    Cliente
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Nome
                      </Typography>
                      <Typography variant="body2" fontWeight="medium" sx={{ cursor: 'pointer' }} onClick={() => navigate(`/clients/${ticket.cliente?.id}`)}>
                        {ticket.cliente?.nome || '-'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Contato
                      </Typography>
                      <Typography variant="body2">
                        {formatarPhone(ticket.cliente?.contatos?.map((contato) => contato.numero).join(', ') || '-')}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Informações de Abertura e Atribuição */}
              <Grid item xs={12} sm={6}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? '#070b14' : 'grey.50',
                    border: '1px solid',
                    borderColor: 'divider',
                    height: '100%',
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom fontWeight="bold" color="primary" sx={{ mb: 0.5 }}>
                    <PersonIcon sx={{ mr: 0.5, verticalAlign: 'middle', fontSize: '1rem' }} />
                    Informações de Abertura
                  </Typography>
                  <Divider sx={{ my: 1 }} />
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Aberto Por
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {ticket.usuario_abriu?.nome || 'Não informado'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                        Atribuído Para
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        <Typography variant="body2" fontWeight="medium" sx={{ flex: 1, minWidth: '120px' }}>
                          {ticket.atribuido_para_funcionario?.nome || 'Não atribuído'}
                        </Typography>
                        {ticket.atribuido_para_funcionario?.cargo && (
                          <Chip
                            label={ticket.atribuido_para_funcionario.cargo}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: '0.7rem' }}
                          />
                        )}
                        {ticket.status !== 'fechado' && ticket.status !== 'cancelado' && (
                          <>
                            <Button
                              variant="contained"
                              size="small"
                              sx={{ ml: 1, px: 0, py: 0, fontSize: '0.7rem' }}
                              color="primary"
                              onClick={() => setAtribuidoParaModalOpen(true)}
                              disabled={loading || removingAssignment}
                            >
                              {ticket.atribuido_para_funcionario ? 'Alterar' : 'Atribuir'}
                            </Button>
                            {ticket.atribuido_para_funcionario && (
                              <Button
                                variant="outlined"
                                size="small"
                                sx={{ ml: 1, px: 0, py: 0, fontSize: '0.7rem' }}
                                color="error"
                                onClick={handleRemoveAssignment}
                                disabled={loading || removingAssignment}
                              >
                                Remover
                              </Button>
                            )}
                          </>
                        )}
                      </Box>
                      {ticket.atribuido_para_funcionario?.email && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          {ticket.atribuido_para_funcionario.email}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Solicitado Por
                      </Typography>
                      <Typography variant="body2">
                        {ticket.quem_solicitou || 'Não informado'}
                      </Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Informações do Serviço */}
              {ticket.contrato_servico && (
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      bgcolor: (theme) => theme.palette.mode === 'dark' ? '#070b14' : 'grey.50',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    <Typography variant="subtitle2" gutterBottom fontWeight="bold" color="primary" sx={{ mb: 0.5 }}>
                      <DescriptionIcon sx={{ mr: 0.5, verticalAlign: 'middle', fontSize: '1rem' }} />
                      Informações do Serviço
                    </Typography>
                    <Divider sx={{ my: 1 }} />
                    <Grid container spacing={1.5}>
                      {ticket.contrato_servico.usuario_pppoe && (
                        <Grid item xs={12} sm={4}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Usuário PPPoE
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {ticket.contrato_servico.usuario_pppoe}
                          </Typography>
                        </Grid>
                      )}
                      {ticket.contrato_servico.status_conexao && (
                        <Grid item xs={12} sm={4}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Status de Conexão
                          </Typography>
                          <Chip
                            label={ticket.contrato_servico.status_conexao === 'conectado' ? 'Conectado' : 'Desconectado'}
                            color={ticket.contrato_servico.status_conexao === 'conectado' ? 'success' : 'error'}
                            size="small"
                            sx={{ height: '24px', fontSize: '0.75rem', fontWeight: 600 }}
                          />
                        </Grid>
                      )}
                      {ticket.contrato_servico.plano && (
                        <Grid item xs={12} sm={4}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Plano
                          </Typography>
                          <Typography variant="body2" fontWeight="medium">
                            {ticket.contrato_servico.plano.nome}
                          </Typography>
                        </Grid>
                      )}
                      {(ticket.contrato_servico.endereco_inst || ticket.contrato_servico.cidade_inst || ticket.contrato_servico.estado_inst) && (
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Endereço
                          </Typography>
                          <Typography variant="body2">
                            {[
                              ticket.contrato_servico.endereco_inst,
                              ticket.contrato_servico.numero_inst,
                              ticket.contrato_servico.bairro_inst,
                              ticket.contrato_servico.cidade_inst,
                              ticket.contrato_servico.estado_inst,
                              ticket.contrato_servico.cep_inst ? `CEP: ${ticket.contrato_servico.cep_inst}` : null,
                            ]
                              .filter(Boolean)
                              .join(', ')}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                </Grid>
              )}

              {/* Informações Adicionais */}
              {(() => {
                const anexos = normalizeAnexos(ticket.anexos);
                const hasAnexos = anexos.length > 0;

                if (!hasAnexos) {
                  return null;
                }

                return (
                  <Grid item xs={12}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 1.5,
                        bgcolor: (theme) => theme.palette.mode === 'dark' ? '#070b14' : 'grey.50',
                        border: '1px solid',
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="subtitle2" gutterBottom fontWeight="bold" color="primary" sx={{ mb: 0.5 }}>
                        <DescriptionIcon sx={{ mr: 0.5, verticalAlign: 'middle', fontSize: '1rem' }} />
                        Anexos
                      </Typography>
                      <Divider sx={{ my: 1 }} />
                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {anexos.map((anexo, index) => (
                          <Chip
                            key={index}
                            label={anexo}
                            size="small"
                            variant="outlined"
                            clickable
                            onClick={() => window.open(anexo, '_blank')}
                          />
                        ))}
                      </Box>
                    </Paper>
                  </Grid>
                );
              })()}

              {/* Movimentações */}
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    bgcolor: (theme) => theme.palette.mode === 'dark' ? '#070b14' : 'grey.50',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle2" fontWeight="bold" color="primary">
                      <HistoryIcon sx={{ mr: 0.5, verticalAlign: 'middle', fontSize: '1rem' }} />
                      Movimentações ({movimentacoes.length})
                    </Typography>
                    {ticket.status !== 'fechado' && ticket.status !== 'cancelado' && (
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<AddIcon />}
                        onClick={() => setMovimentacaoModalOpen(true)}
                        disabled={loadingMovimentacoes}
                      >
                        Nova Movimentação
                      </Button>
                    )}
                  </Box>
                  <Divider sx={{ my: 1 }} />
                  {loadingMovimentacoes ? (
                    <Box display="flex" justifyContent="center" py={2}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : movimentacoes.length === 0 ? (
                    <Alert severity="info">
                      Nenhuma movimentação registrada ainda.
                    </Alert>
                  ) : (
                    <List sx={{ maxHeight: 350, overflow: 'auto', py: 0 }}>
                      {movimentacoes.map((movimentacao) => (
                        <ListItem
                          key={movimentacao.id}
                          sx={{
                            bgcolor: (theme) => theme.palette.mode === 'dark' ? 'background.paper' : '#ffffff',
                            mb: 0.75,
                            py: 1,
                            borderRadius: 1,
                            border: '1px solid',
                            borderColor: 'divider',
                          }}
                        >
                          <ListItemAvatar sx={{ minWidth: '36px' }}>
                            {movimentacao.usuario?.avatar ? (
                              <Avatar
                                src={avatarService.getAvatarUrl(movimentacao.usuario?.avatar)}
                                sx={{
                                  width: 32,
                                  height: 32,
                                  bgcolor: 'primary.main',
                                  fontSize: '0.875rem',
                                }}
                              />
                            ) : (
                              <Avatar
                                sx={{
                                  width: 32,
                                  height: 32,
                                  bgcolor: 'primary.main',
                                  fontSize: '0.875rem',
                                }}
                              >
                                {movimentacao.usuario?.nome?.charAt(0).toUpperCase() || 'U'}
                              </Avatar>
                            )}
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" justifyContent="space-between">
                                <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.875rem' }}>
                                  {movimentacao.usuario?.nome || 'Usuário desconhecido'}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ fontSize: '0.7rem', ml: 2 }}
                                >
                                  {formatDateTime(movimentacao.createdAt)}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Typography
                                variant="body2"
                                sx={{
                                  mt: 0.75,
                                  fontSize: '0.8125rem',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {movimentacao.informacoes}
                              </Typography>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Box display="flex" flex={1} alignItems="center">
          {ticket && ticket.status !== 'fechado' && ticket.status !== 'cancelado' && (
            <Button
              onClick={handleOpenCloseTicketModal}
              disabled={loading}
              variant="contained"
              color="error"
              startIcon={<CloseIcon />}
              sx={{ mr: 1 }}
              size="small"
            >
              Fechar Chamado
            </Button>
          )}
          {ticket && ticket.status !== 'fechado' && ticket.status !== 'cancelado' && ticket.status !== 'resolvido' && (
            <Button
              onClick={handleOpenCancelTicketModal}
              disabled={loading}
              variant="outlined"
              color="error"
              startIcon={<CloseIcon />}
              size="small"
            >
              Cancelar Chamado
            </Button>
          )}
          {ticket && ticket.status === 'resolvido' && (
            <Button
              onClick={handleReturnTicket}
              disabled={loading}
              variant="contained"
              color="success"
              startIcon={<ArrowBackIcon />}
              size="small"
            >
              Retornar
            </Button>
          )}
          <Box flex={1} />
          <Button
            onClick={onClose}
            startIcon={<CloseIcon />}
          >
            Fechar
          </Button>
        </Box>
      </DialogActions>

      {/* Modal de Nova Movimentação */}
      {ticket && (
        <NovaMovimentacaoModal
          open={movimentacaoModalOpen}
          onClose={() => setMovimentacaoModalOpen(false)}
          onSuccess={() => {
            loadMovimentacoes();
          }}
          chamadoId={ticket.id}
        />
      )}

      {/* Modal de Atribuição de Chamado */}
      <Dialog
        open={atribuidoParaModalOpen}
        onClose={handleCloseAssignModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">
              Atribuir Chamado
            </Typography>
            <IconButton
              onClick={handleCloseAssignModal}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ py: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id="funcionario-select-label">Funcionário</InputLabel>
              <Select
                labelId="funcionario-select-label"
                id="funcionario-select"
                value={selectedFuncionarioId}
                label="Funcionário"
                onChange={(e) => setSelectedFuncionarioId(e.target.value as number | '')}
                disabled={loadingFuncionarios || assigningTicket}
              >
                {loadingFuncionarios ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    Carregando funcionários...
                  </MenuItem>
                ) : funcionarios.length === 0 ? (
                  <MenuItem disabled>
                    Nenhum funcionário ativo encontrado
                  </MenuItem>
                ) : (
                  funcionarios.map((funcionario) => (
                    <MenuItem key={funcionario.id} value={funcionario.id}>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {funcionario.nome}
                        </Typography>
                        {funcionario.cargo && (
                          <Typography variant="caption" color="text.secondary">
                            {funcionario.cargo}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Button
            onClick={handleCloseAssignModal}
            disabled={assigningTicket}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleAssignTicket}
            variant="contained"
            color="primary"
            disabled={!selectedFuncionarioId || loadingFuncionarios || assigningTicket}
            startIcon={assigningTicket ? <CircularProgress size={16} /> : null}
          >
            {assigningTicket ? 'Atribuindo...' : 'Atribuir'}
          </Button>
        </DialogActions>
      </Dialog>

    </Dialog>
  );
};

export default ChamadosInfoView;

